import React from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import RefreshIcon from '@material-ui/icons/RefreshRounded';
import NamedBox from '../atoms/NamedBox';
import ProgressLine from '../atoms/ProgressLine';
import Tooltip from '../atoms/Tooltip';
import NilValuesTooltip from '../molecules/NilValuesTooltip';
import On3LogoIcon from '../icons/On3LogoIcon';
import HistoryIcon from '../icons/HistoryIcon';
import { COLOR_BACKGROUND_WARM, COLOR_BACKGROUND_LIGHT, COLOR_BORDER, COLOR_GREEN, COLOR_MIDDLE_GRAY, COLOR_TEXT, COLOR_WHITE, COLOR_BLUE } from '../styles/colors';
import { FONT_PROXIMA_NOVA } from '../styles/fonts';
import Player from '../types/Player';
import { on3NilValuesResponse } from '../types/PlayerNILValues';
import { datetimeToDate } from '../services/converter';
import Chip from '@material-ui/core/Chip';
import { Divider } from '@material-ui/core';
import InfoIcon from '../icons/InfoIcon';
import ArrowUpCircleIcon from '../icons/ArrowUpCircleIcon';
import ArrowDownIcon from '../icons/ArrowDownIcon';
import Team from '../types/Team';
import NotAvailable from '../atoms/NotAvailable';
import SocialXIcon from '../icons/SocialXIcon';
import SocialInstagramIcon from '../icons/SocialInstagramIcon';
import SocialTikTokIcon from '../icons/SocialTikTokIcon';
import { formatValueInK } from '../services/user';
import { ON3_NIL_VALUATION_TOOLTIPS } from '../services/tooltips';

interface On3NilValuationProps {
  className?: string;
  player?: Player;
  playerNilValues?: on3NilValuesResponse;
  playerOn3Date?: string;
  selectedTeam?: Team | null;
}

const useStyles = makeStyles((theme: Theme) => ({
  valuationWrapper: {
  },
  valuationContent: {
    border: `1px solid ${COLOR_MIDDLE_GRAY}`,
  },
  valuationSection: {
    overflow: 'auto',
  },
  valuationContainer: {
    display: 'grid',
    gridTemplateColumns: 'minmax(240px, 280px) minmax(500px, auto) minmax(340px, 476px)',

    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '180px minmax(260px, auto) 180px',
    },
  },
  headerContainer:{
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(.5, 1),
    minHeight: '64px',
    background: COLOR_BACKGROUND_WARM,
    padding: theme.spacing(2),

    [theme.breakpoints.down('xs')]: {
      flexWrap: 'wrap',
    },
  },
  visibilityHeader: {
    justifyContent: 'space-between',
  },
  headerText: {
    fontSize: theme.typography.pxToRem(20),
    lineHeight: 1.1,
    fontWeight: 700,

    [theme.breakpoints.down('xs')]: {
      fontSize: theme.typography.pxToRem(18),
    },
  },
  on3Icon: {
    flexShrink: 0,

    '& svg': {
      display: 'block',
      width: 40,
      height: 'auto',
    },

    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  infoIcon: {
    flexShrink: 0,
    width: 28,
    height: 28,
    cursor: 'help',
  },
  visibilityHeaderText: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  visibilityHeaderIcon: {
    display: 'flex',
    alignItems: 'center',
    width: 24,
    height: 'auto',
  },
  valueCol: {
    borderRight: `1px solid ${COLOR_BORDER}`,
  },
  nilHistoryIndicator: {
    height: 24,
    width: 24,

    '& svg': {
      display: 'block',
    },
  },
  rowWrapper: {
    display: 'flex',
    flexDirection: 'column',

    [theme.breakpoints.down('xs')]: {
      borderRight: `1px solid ${COLOR_BORDER}`,

      '&:last-child': {
        borderRight: 0,
      },
    },
  },
  row: {
    flexBasis: '50%',

    '&:nth-child(even)': {
      background: COLOR_BACKGROUND_LIGHT,
    },

    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1, 2),
    },
  },
  rowCell: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    borderRight: `1px solid ${COLOR_BORDER}`,

    '&:last-child': {
      borderRight: 0,
    },

    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(.5),
      padding: 0,
      border: 0,

      '&:first-child': {
        marginTop: 0,
      },
    },
  },
  rowCellCenter: {
    justifyContent: 'center',

    [theme.breakpoints.down('xs')]: {
      justifyContent: 'flex-start',
    },
  },
  barRow: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  barLabel: {
    marginRight: theme.spacing(2),
    minWidth: 60,
  },
  chip: {},
  positionChip: {
    width: '100%',
    height: 36,
    borderRadius: 40,
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 600,
    color: COLOR_WHITE,
  },
  valueWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    height: '100%',
    padding: theme.spacing(2),
    fontSize: theme.typography.pxToRem(32),
    fontWeight: 700,
    color: COLOR_GREEN,

    [theme.breakpoints.down('xs')]: {
      fontSize: theme.typography.pxToRem(26),
    },
  },
  valueWrapperDown: {
    color: COLOR_TEXT,
  },
  progressLine: {
    paddingLeft: 0,
  },
  averagesHeader: {
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
  },
  averageLabel: {
    fontWeight: 700,
    color: COLOR_GREEN,
  },
  logoLabelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    lineHeight: 1.2,
  },
  socialRow: {
    display: 'flex',
    borderTop: `1px solid ${COLOR_MIDDLE_GRAY}`,
    
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  socialItem: {
    flexBasis: '33.3333%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    minHeight: '150px',
    padding: theme.spacing(2),
    borderRight: `1px solid ${COLOR_BORDER}`,

    '&:last-child': {
      borderRight: 0,
    },
    
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(1),
    },

    [theme.breakpoints.down('xs')]: {
      minHeight: 0,
      borderRight: 0,
      borderBottom: `1px solid ${COLOR_BORDER}`,

      '&:last-child': {
        borderBottom: 0,
      },
    },
  },
  socialIcon: {
    height: 36,
    width: 36,

    '& svg': {
      display: 'block',
    },
    
    [theme.breakpoints.down('sm')]: {
      width: 26,
      height: 26,
    },
  },
  socialLabel: {
    display: 'flex',
    flexDirection: 'column',
    marginRight: theme.spacing(0.5),
    fontSize: theme.typography.pxToRem(16),
  },
  socialValue: {
    fontWeight: 700,
  },
  socialLabelWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  tiktokInfoWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
  },
  tiktokTextWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    fontSize: theme.typography.pxToRem(14),
  },
  socialStatRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(0.5),
  },
  socialStatLabel: {
    fontWeight: 400,
  },
  socialStatValue: {
    fontWeight: 700,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
    textAlign: 'center',
    borderTop: `1px solid ${COLOR_BORDER}`,
    background: COLOR_BACKGROUND_WARM,
    fontWeight: 600,

    '& a': {
      color: COLOR_BLUE,
    },
  },
}), { name: 'On3NilValuation' });

function formatCurrencyTwoDecimals(value?: number | null): string {
  if (value === null || value === undefined) return 'N/A';
  return `$${value.toFixed(2)}`;
}

export default function On3NilValuation(props: On3NilValuationProps) {
  const {
    className,
    playerNilValues,
    selectedTeam,
    player
  } = props;
  const classes = useStyles();
  const playerAny = player as any;

  const xFollowers = player?.twitterFollowers;
  const xAvgLikes = playerAny?.twitterAvgLikes ?? playerAny?.xAvgLikes ?? playerAny?.xAverageLikes;
  const instagramFollowers = player?.instagramFollowers;
  const instagramAvgLikes = playerAny?.instagramAvgLikes ?? playerAny?.instagramAverageLikes;
  const tikTokFollowers = player?.tikTokFollowers;
  const tikTokAvgLikes = playerAny?.tikTokAvgLikes ?? playerAny?.tiktokAvgLikes ?? playerAny?.tikTokAverageLikes ?? playerAny?.tiktokAverageLikes;

  const playerData = playerNilValues;

  const rows = [
    {
      id: 'valuation',
      title: 'NIL Valuation',
      visibility: 'Public',
      tooltip: ON3_NIL_VALUATION_TOOLTIPS.NIL_VALUATION,
      positionCode: playerData?.positionCode,
      value: playerData?.on3PublicNILValuation?.value,
      direction: playerData?.on3PublicNILValuation?.direction,
      allPercentile: playerData?.on3PublicNILValuation?.allPercentile,
      allRank: playerData?.on3PublicNILValuation?.allRank,
      allRankTotal: playerData?.on3PublicNILValuation?.allRankTotal,
      positionPercentile: playerData?.on3PublicNILValuation?.positionPercentile,
      positionRank: playerData?.on3PublicNILValuation?.positionRank,
      positionRankTotal: playerData?.on3PublicNILValuation?.positionRankTotal,
      teamAllAverageValue: playerData?.on3PublicNILValuation?.teamAllAverageValue,
      teamAllRank: playerData?.on3PublicNILValuation?.teamAllRank,
      teamAllRankTotal: playerData?.on3PublicNILValuation?.teamAllRankTotal,
      teamPositionAverageValue: playerData?.on3PublicNILValuation?.teamPositionAverageValue,
      teamPositionRank: playerData?.on3PublicNILValuation?.teamPositionRank,
      teamPositionRankTotal: playerData?.on3PublicNILValuation?.teamPositionRankTotal,
    },
    {
      id: 'roster',
      title: 'Roster Value',
      visibility: 'Private',
      tooltip: ON3_NIL_VALUATION_TOOLTIPS.ROSTER_VALUE,
      positionCode: playerData?.positionCode,
      value: playerData?.on3RosterValue?.value,
      direction: playerData?.on3RosterValue?.direction,
      allPercentile: playerData?.on3RosterValue?.allPercentile,
      allRank: playerData?.on3RosterValue?.allRank,
      allRankTotal: playerData?.on3RosterValue?.allRankTotal,
      positionPercentile: playerData?.on3RosterValue?.positionPercentile,
      positionRank: playerData?.on3RosterValue?.positionRank,
      positionRankTotal: playerData?.on3RosterValue?.positionRankTotal,
      teamAllAverageValue: playerData?.on3RosterValue?.teamAllAverageValue,
      teamAllRank: playerData?.on3RosterValue?.teamAllRank,
      teamAllRankTotal: playerData?.on3RosterValue?.teamAllRankTotal,
      teamPositionAverageValue: playerData?.on3RosterValue?.teamPositionAverageValue,
      teamPositionRank: playerData?.on3RosterValue?.teamPositionRank,
      teamPositionRankTotal: playerData?.on3RosterValue?.teamPositionRankTotal,
    },
    {
      id: 'nil',
      title: 'NIL Value',
      visibility: 'Private',
      tooltip: ON3_NIL_VALUATION_TOOLTIPS.NIL_VALUE,
      positionCode: playerData?.positionCode,
      value: playerData?.on3PrivateNILValue?.value,
      direction: playerData?.on3PrivateNILValue?.direction,
      allPercentile: playerData?.on3PrivateNILValue?.allPercentile,
      allRank: playerData?.on3PrivateNILValue?.allRank,
      allRankTotal: playerData?.on3PrivateNILValue?.allRankTotal,
      positionPercentile: playerData?.on3PrivateNILValue?.positionPercentile,
      positionRank: playerData?.on3PrivateNILValue?.positionRank,
      positionRankTotal: playerData?.on3PrivateNILValue?.positionRankTotal,
      teamAllAverageValue: playerData?.on3PrivateNILValue?.teamAllAverageValue,
      teamAllRank: playerData?.on3PrivateNILValue?.teamAllRank,
      teamAllRankTotal: playerData?.on3PrivateNILValue?.teamAllRankTotal,
      teamPositionAverageValue: playerData?.on3PrivateNILValue?.teamPositionAverageValue,
      teamPositionRank: playerData?.on3PrivateNILValue?.teamPositionRank,
      teamPositionRankTotal: playerData?.on3PrivateNILValue?.teamPositionRankTotal,
    },
  ];

  return (
    <div className={classes.valuationWrapper}>
      <div className={classes.valuationContent}>
        {
          rows.map((row, idx) => (
            <div key={idx} className={classes.valuationSection}>
              <div className={classes.valuationContainer}>
                <div className={classes.header}>
                  <On3LogoIcon className={classes.on3Icon} />

                  <span className={classes.headerText}>{row.title}</span>

                  <Tooltip title={row.tooltip} placement="top">
                    <InfoIcon className={classes.infoIcon} />
                  </Tooltip>
                </div>

                <div className={clsx(classes.header, classes.visibilityHeader)}>
                  <span className={classes.visibilityHeaderText}>{row.visibility}</span>
                  <HistoryIcon className={classes.visibilityHeaderIcon} />
                </div>

                <div className={classes.header}>
                  {selectedTeam?.logo247 && (
                    <div className={classes.logoLabelRow}>
                      <img
                        src={selectedTeam.logo247}
                        alt={selectedTeam.name || 'Team Logo'}
                        width={20}
                        height={20}
                      />
                    </div>
                  )}
                  <span className={classes.headerText}>Averages</span>
                </div>

                <div className={clsx(classes.valueCol)}>
                  <div className={clsx(classes.valueWrapper, row?.direction === 'down' && classes.valueWrapperDown)}>
                    {/* <span>${row.value}</span>
                    {
                      row?.direction === 'down' ?
                        <ArrowDownIcon className={classes.nilHistoryIndicator} />
                      :
                        <ArrowUpCircleIcon className={classes.nilHistoryIndicator} />
                    } */}
                    <NilValuesTooltip valuationTextStyle={{ fontWeight: 'bold' }} on3PublicNILValuation={row.value}  />
                  </div>
                </div>

                <div className={classes.rowWrapper}>
                  <Grid container className={classes.row}>
                    <Grid item xs={12} sm={8} className={classes.rowCell}>
                      <div className={classes.barRow}>
                        <span className={classes.barLabel}>
                          <Chip label={'All'} className={clsx(classes.chip, classes.positionChip)} color="primary" />
                        </span>
                        
                        <ProgressLine
                          className={classes.progressLine}
                          value={row.allPercentile || 0}
                        />
                      </div>
                    </Grid>

                    <Grid item xs={12} sm={4} className={classes.rowCell}>
                      <div>Rank: <b>#{row.allRank}</b> of {row.allRankTotal}</div>
                    </Grid>
                  </Grid>

                  <Grid container className={classes.row}>
                    <Grid item xs={12} sm={8} className={classes.rowCell}>
                      <div className={classes.barRow}>
                        <span className={classes.barLabel}>
                            <Chip label={row.positionCode} className={clsx(classes.chip, classes.positionChip)} color="primary" />
                        </span>
                        <ProgressLine
                          className={classes.progressLine}
                          value={row.positionPercentile || 0}
                        />
                      </div>
                    </Grid>

                    <Grid item xs={12} sm={4} className={classes.rowCell}>
                      <div>Rank: <b>#{row.positionRank}</b> of {row.positionRankTotal}</div>
                    </Grid>
                  </Grid>
                </div>

                <div className={classes.rowWrapper}>
                  <Grid container className={classes.row}>
                    <Grid item xs={12} sm={4} className={clsx(classes.rowCell, classes.rowCellCenter)}>
                      <div className={classes.averageLabel}>${formatValueInK(row.teamAllAverageValue)}</div>
                    </Grid>

                    <Grid item xs={12} sm={4} className={clsx(classes.rowCell, classes.rowCellCenter)}>
                      {selectedTeam?.logo247 && (
                          <div className={classes.logoLabelRow}>
                            <img
                              src={selectedTeam.logo247}
                              alt={selectedTeam.name || 'Team Logo'}
                              width={20}
                              height={20}
                            />
                          </div>
                        )}
                      <div style={{paddingLeft: '4px'}}>Players</div>
                    </Grid>

                    <Grid item xs={12} sm={4} className={clsx(classes.rowCell, classes.rowCellCenter)}>
                      <div>Rank: <b>#{row.teamAllRank}/{row.teamAllRankTotal}</b></div>
                    </Grid>
                  </Grid>

                  <Grid container className={classes.row}>
                    <Grid item xs={12} sm={4} className={clsx(classes.rowCell, classes.rowCellCenter)}>
                      <div className={classes.averageLabel}>${formatValueInK(row.teamPositionAverageValue)}</div>
                    </Grid>

                    <Grid item xs={12} sm={4} className={clsx(classes.rowCell, classes.rowCellCenter)}>
                      {selectedTeam?.logo247 && (
                          <div className={classes.logoLabelRow}>
                            <img
                              src={selectedTeam.logo247}
                              alt={selectedTeam.name || 'Team Logo'}
                              width={20}
                              height={20}
                            />
                          </div>
                        )}
                      <div style={{paddingLeft: '4px'}}>{row.positionCode}</div>
                    </Grid>

                    <Grid item xs={12} sm={4} className={clsx(classes.rowCell, classes.rowCellCenter)}>
                      <div>Rank: <b>#{row.teamPositionRank}/{row.teamPositionRankTotal}</b></div>
                    </Grid>
                  </Grid>
                </div>
              </div>
            </div>
          ))
        }

        <div className={classes.socialRow}>
          <div className={classes.socialItem}>
            <div className={classes.socialIcon}>
              <SocialXIcon />
            </div>

            <div className={classes.socialLabel}>
              <span>Followers: <b>{xFollowers ?? <NotAvailable />}</b></span>
            </div>
          </div>

          <div className={classes.socialItem}>
            <div className={classes.socialIcon}>
              <SocialInstagramIcon />
            </div>

            <div className={classes.socialLabel}>
              <span>Followers: <b>{instagramFollowers ?? <NotAvailable />}</b></span>
              <span>Avg. Likes: <b>{instagramAvgLikes ?? <NotAvailable />}</b></span>
            </div>
          </div>
          
          <div className={classes.socialItem}>
            <div className={classes.socialIcon}>
              <SocialTikTokIcon />
            </div>

            <div className={classes.socialLabel}>
              <span>Subscribers: <b>{tikTokFollowers ?? <NotAvailable />}</b></span>
              <span>Avg. Likes: <b>{tikTokAvgLikes ?? <NotAvailable />}</b></span>
            </div>
          </div>
        </div>

        <div className={classes.footer}>
          <div>
            <span>All data provided by <a href="https://tpvanalytics.com/" target="_blank">Total Player Value</a>.</span>
          </div>
        </div>
      </div>
    </div>
  );
}

